/**
 * Select dropdown field component.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { FieldWrapper, getFieldAriaIds } from "./FieldWrapper";
import type { FieldProps } from "./types";

export function SelectField({
  field,
  value,
  touched,
  error,
  label,
  placeholder,
  description,
  extendedDescription,
  onChange,
  getOptionLabel,
}: FieldProps) {
  const hasError = touched && !!error;
  const { descriptionId, extendedDescriptionId, errorId } = getFieldAriaIds(field.name);

  // Build aria-describedby based on available content
  const ariaDescribedByParts: string[] = [];
  if (hasError) {
    ariaDescribedByParts.push(errorId);
  } else {
    if (description) ariaDescribedByParts.push(descriptionId);
    if (extendedDescription) ariaDescribedByParts.push(extendedDescriptionId);
  }
  const ariaDescribedBy = ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

  return (
    <FieldWrapper
      name={field.name}
      label={label}
      required={field.validation?.required}
      hasError={hasError}
      error={error}
      description={description}
      extendedDescription={extendedDescription}
    >
      <Select
        value={value as string}
        onValueChange={(newValue) => onChange(field.name, newValue)}
        required={field.validation?.required}
      >
        <SelectTrigger
          error={hasError}
          id={field.name}
          aria-describedby={ariaDescribedBy}
        >
          <SelectValue
            placeholder={placeholder || `Select ${label.toLowerCase()}`}
          />
        </SelectTrigger>
        <SelectContent>
          {field.options?.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {getOptionLabel
                ? getOptionLabel(option.value, option.label)
                : option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldWrapper>
  );
}
